import { useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import { examsSelectors } from '../slices/exams/examsSlice';
import type { RootState } from '../store/store';
import type { UserExam } from '../types/User';
import { hapticsImpactLight } from '../utils/haptics';
import { NCLEX_EXAM_ID } from '../utils/examUtils';
import { MCGRAW_HILL_HANDBOOK_EXAM_ID } from './mcgrawHill';
import { selectIsHandbookActive } from './nursingCourses';
import { useOpenDutCourse } from './useOpenDutCourse';

interface ExamMenuGroup {
  id: string;
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: ReactNode;
}

interface DutExamMenuProps {
  /** The exam menu's own option: selecting it makes the exam primary. */
  renderExamOption: (exam: UserExam) => ReactNode;
  /** The exam menu's own collapsible group. */
  renderExamGroup: (group: ExamMenuGroup) => ReactNode;
  /** Closes the menu. */
  onClose: () => void;
}

/**
 * mcgraw-hill (ORA-1263, ORA-1268): the Nursing vertical's exam menu.
 * NCLEX-RN (when the account holds it) through the menu's own option, then a
 * Courses group with Nursing Pharmacology (the handbook exam), which opens
 * the course as the Courses page does. The group starts expanded while the
 * course is the active exam; the menu mounts this afresh each time it opens.
 */
const DutExamMenu = ({
  renderExamOption,
  renderExamGroup,
  onClose,
}: DutExamMenuProps) => {
  const nclexExam = useSelector((state: RootState) =>
    examsSelectors.selectById(state, NCLEX_EXAM_ID)
  );

  const course = useSelector((state: RootState) =>
    examsSelectors.selectById(state, MCGRAW_HILL_HANDBOOK_EXAM_ID)
  );

  const isCourseActive = useSelector(selectIsHandbookActive);
  const [isCoursesExpanded, setIsCoursesExpanded] = useState(isCourseActive);
  const { openCourse } = useOpenDutCourse();

  if (!nclexExam && !course) {
    return (
      <p className="m-0 px-3 py-2 text-sm text-fg-secondary">
        No exams yet for Nursing
      </p>
    );
  }

  return (
    <>
      {nclexExam && renderExamOption(nclexExam)}
      {course &&
        renderExamGroup({
          id: 'dut-courses-panel',
          label: 'Courses',
          isExpanded: isCoursesExpanded,
          onToggle: () => setIsCoursesExpanded((value) => !value),
          children: (
            <Button
              variant="ghost"
              pressed={isCourseActive}
              context="menu"
              onClick={() => {
                hapticsImpactLight();
                onClose();
                void openCourse();
              }}
              tabIndex={0}
              aria-label={`Select ${course.name}`}
            >
              {course.name}
            </Button>
          ),
        })}
    </>
  );
};

export default DutExamMenu;
